'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { Lock } from 'lucide-react'

export default function LoginPage() {
  const router = useRouter()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Login failed')
      }

      localStorage.setItem('token', data.token)
      localStorage.setItem('user', JSON.stringify(data.user))

      if (data.user.role === 'PROFESSOR' || data.user.role === 'AUTHOR') {
        router.push('/dashboard/professor')
      } else {
        router.push('/dashboard/student')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'حدث خطأ أثناء تسجيل الدخول')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFB] flex items-center justify-center px-4 dir-rtl">
      <div className="max-w-md w-full">
        <div className="card">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#EAF7F1] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#168A63]" />
            </div>
            <h1 className="text-[34px] font-bold text-[#1F2937] mb-2">
              تسجيل الدخول
            </h1>
            <p className="text-[#6B7280]">
              أدخل بياناتك للوصول إلى حسابك
            </p>
          </div>

          {error && (
            <div
              className="mb-6 p-4 bg-[#FEE2E2] border border-[#FECACA] rounded-lg"
              role="alert"
              aria-live="polite"
            >
              <p className="text-[#DC2626] text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-[#1F2937] mb-2"
              >
                البريد الإلكتروني
              </label>
              <input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#168A63] focus:border-transparent bg-white text-[#1F2937]"
                aria-describedby="email-hint"
              />
              <p id="email-hint" className="mt-2 text-sm text-[#6B7280]">
                أدخل بريدك الإلكتروني المسجل
              </p>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-[#1F2937] mb-2"
              >
                كلمة المرور
              </label>
              <input
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#168A63] focus:border-transparent bg-white text-[#1F2937]"
                aria-describedby="password-hint"
              />
              <p id="password-hint" className="mt-2 text-sm text-[#6B7280]">
                أدخل كلمة المرور الخاصة بك
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-6 bg-[#168A63] text-white font-semibold rounded-lg hover:bg-[#0F6B4A] focus:outline-none focus:ring-4 focus:ring-[#168A63] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-busy={loading}
            >
              {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-[#6B7280]">
              ليس لديك حساب؟{' '}
              <Link
                href="/register"
                className="text-[#168A63] font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-[#168A63] rounded"
              >
                إنشاء حساب جديد
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link
              href="/"
              className="text-sm text-[#6B7280] hover:text-[#1F2937] focus:outline-none focus:ring-2 focus:ring-[#168A63] rounded"
            >
              العودة إلى الصفحة الرئيسية
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
